import { useEffect, useMemo, useRef } from 'react'
import type { GridColDef, GridColumnVisibilityModel, GridPinnedColumns, GridSortModel } from '@mui/x-data-grid-pro'
import { useDebouncedCallback } from 'use-debounce'

import { Time } from '@carsys/enums/time'

import { isValidGridSortItem } from './data-grid-sorting'
import { Direction, ListColumnSetting, ListSettings, Pinned, SaveSettings } from './use-list-service/types'
import { useFeature } from '@cbgms/base/feature-flags'

interface DataGridSettings {
  sortModel: GridSortModel
  columnVisibilityModel: GridColumnVisibilityModel
  columns: GridColDef[]
  pinnedColumns: GridPinnedColumns
}

function mapDataGridSettingsToListSettings({ sortModel, columnVisibilityModel, columns, pinnedColumns }: DataGridSettings): ListSettings {
  const visibleColumns = columns.filter(column => columnVisibilityModel[column.field])

  const listColumns: ListColumnSetting[] = visibleColumns.map((column, index) => {
    return {
      FilterOperator: '',
      FilterValue: '',
      Index: index,
      Name: column.field,
      Pinned: queryPinning(pinnedColumns, column.field),
      SortDirection: querySortDirection(sortModel, column.field),
      SortIndex: querySortIndex(sortModel, column.field),
      Width: column.width ?? 200
    }
  })

  return {
    Columns: listColumns,
    PredefinedSearch: ''
  }
}

function queryPinning(pinnedColumns: GridPinnedColumns, columnName: string): Pinned {
  if (pinnedColumns.left?.includes(columnName)) return Pinned.LEFT
  if (pinnedColumns.right?.includes(columnName)) return Pinned.RIGHT
  return Pinned.CENTER
}

function querySortDirection(sortModel: GridSortModel, columnName: string): Direction | '' {
  const match = sortModel.find(item => item.field === columnName)

  if (match?.sort === 'asc') return Direction.ASCENDING
  if (match?.sort === 'desc') return Direction.DESCENDING
  return ''
}

function querySortIndex(sortModel: GridSortModel, columnName: string): number {
  const index = sortModel.filter(isValidGridSortItem).findIndex(item => item.field === columnName)

  return index === -1 ? 0 : index
}

function useIsSettingsEqual(listSettings: ListSettings): boolean {
  const listSettingsRef = useRef(listSettings)

  // An inequal amount of columns always means the settings won't be equal.
  // In that case we can update the reference and return early.
  if (listSettings.Columns.length !== listSettingsRef.current.Columns.length) {
    listSettingsRef.current = listSettings
    return false
  }

  // Compare every column's properties to the stored reference based on their index.
  // If the properties differ for a specific index the settings won't be equal.
  const equality = listSettings.Columns.every((column, index) => {
    const refColumn = listSettingsRef.current.Columns[index]

    return Object.keys(column).every(key => {
      const typedKey = key as keyof typeof column

      return column[typedKey] === refColumn?.[typedKey]
    })
  })

  listSettingsRef.current = listSettings

  return equality
}

interface UseUpdateListSettingsProps extends DataGridSettings {
  saveSettings: SaveSettings
  hasAllColumnsMadeVisible?: boolean
}

/**
 * This hook syncs the data grid settings to the API. Settings in this case
 * means the following. Visible columns, column order, column pinning,
 * column sorting, column width.
 *
 * There's a few key points to understand.
 *
 * - It checks the equality of the mapped settings using a custom function.
 * - It syncs changed settings to the API using a useEffect hook.
 *
 * The reason why we're using an effect to sync the settings is because
 * we need 4 key pieces of data. We need `columns`, `columVisibilityModel`, `pinnedColumns`
 * and the `sortModel`.
 *
 * We considered using the callbacks that update these states. However there's no easy way
 * to access the required data in those callbacks. We basically have the following two options.
 *
 * - Add the state as dependencies for the callbacks and just use the state.
 * - Use `setState` with a callback and use the previous state in there.
 *
 * Both options become messy pretty quickly.
 *
 * There is a third option which would be using MUI's internal API using a ref.
 * But we would be depending on the MUI data grid internals quite heavily.
 * So this option is the least preferred one which is why we didn't use it.
 */
export function useSyncListSettings({
  columns,
  columnVisibilityModel,
  pinnedColumns,
  sortModel,
  hasAllColumnsMadeVisible,
  saveSettings
}: UseUpdateListSettingsProps): void {
  const listSettings = useMemo(() => {
    return mapDataGridSettingsToListSettings({
      columns,
      columnVisibilityModel,
      pinnedColumns,
      sortModel
    })
  }, [columns, columnVisibilityModel, pinnedColumns, sortModel])

  const isSupplierOrderEnabled = useFeature('fr0079_enable_supplier_order')
  const initialSettings = useRef(true)
  const hasAllColumnsMadeVisibleRef = useRef(hasAllColumnsMadeVisible)
  hasAllColumnsMadeVisibleRef.current = hasAllColumnsMadeVisible
  const debouncedSaveSettings = useDebouncedCallback(saveSettings, Time.Millisecond * 300)
  // This equality check is needed because we can't determine equality
  // by only checking the references. This is due to the list settings
  // being recreated if any of the dependencies update. This can potentially
  // happen several times in short succession eventhough it would create
  // the same settings.
  const listSettingsAreEqual = useIsSettingsEqual(listSettings)

  useEffect(() => {
    if (listSettingsAreEqual) return
    // If we have settings for the first time that aren't equal don't update them yet
    // these will be the initial settings we fetched from the backend.
    if (initialSettings.current) {
      initialSettings.current = false
      // On page navigation or the component mount, the Show All Columns never triggers
      // the save settings API because the initialSettings.current remains true
      if (!isSupplierOrderEnabled || !hasAllColumnsMadeVisibleRef.current) return
    }

    debouncedSaveSettings(listSettings)
  }, [listSettings, listSettingsAreEqual])
}
